// Moving a whole SECTION between its own styling and a named `section` Storefront Style, in both
// directions: capture what a section looks like into a style's parts, and point a section at a style.
//
// The per-bag pickers on the Appearance tab already attach a style to one bag at a time. These two are the
// whole-section operations, which are what "a section template" actually means to somebody looking at a
// page: the whole look, saved once and reusable.
//
// Kept out of stylesConfig.ts on purpose — that file is the hand-mirror of the shopper renderer's helper and
// knows nothing about IStorefrontSection. This is admin-only, section-shaped, and pure so both can be tested
// without mounting an editor.

import { IStorefrontSection, IStorefrontStyle, IStorefrontStyleParts } from '../../../../models/Storefront';
import {
    findStyle,
    getStyleRef,
    SECTION_OVERRIDE_KEYS,
    SECTION_OVERRIDE_PARTS,
    STYLE_REF_KEY,
    stripStyleRef,
    styleTargetForKey,
} from '../stylesConfig';

/**
 * The section's style bags, paired with the part of a `section` style each one is. Derived from
 * STYLE_TARGET_BY_KEY rather than spelled out again, so the pairing keeps living in one place.
 *
 * `productStyleProps` is NOT here: it targets a CARD style, not a section one (see the target map). A
 * section style does carry a `product` part, but nothing anywhere resolves it, so writing a section's card
 * styling into it would put the data somewhere it can never be read from.
 */
export const SECTION_STYLE_BAGS = [
    'styleProps',
    'titleStyleProps',
    'headerStyleProps',
    'footerStyleProps',
] as const;

export type SectionStyleBag = typeof SECTION_STYLE_BAGS[number];

/** The `section` style part a given bag supplies, e.g. `titleStyleProps` → `title`. */
export const partForBag = (bag: SectionStyleBag): string | undefined => styleTargetForKey(bag)?.part;

/**
 * The section pointed at a named section style: each bag becomes the reference and nothing else.
 *
 * REPLACING rather than merging is the point. A `$styleRef` resolves UNDERNEATH an element's own
 * properties, so keeping the local styling would leave the style hidden behind exactly the hand-tuning it
 * was picked to replace, and applying it would appear to do nothing. Mirrors
 * StorefrontTemplatesHandler.ApplySectionStyleRef on the server; change one and change the other.
 */
export const sectionWithStyleRef = (section: IStorefrontSection, styleId: string): IStorefrontSection => {
    const next = { ...section };
    for (const bag of SECTION_STYLE_BAGS) next[bag] = { [STYLE_REF_KEY]: styleId };
    // Every page-level layer too (AC-1165), so the section follows the style below its band as well as
    // above it. A reference is written for EVERY override key, not only the ones the style happens to
    // carry a part for: a key whose part is missing resolves to the page's own value unchanged, and
    // writing them all is what lets the section pick up a part the style gains later — which is the
    // whole reason to reference a style instead of copying it.
    next.styleOverrides = Object.fromEntries(
        SECTION_OVERRIDE_KEYS.map((key) => [key, { [STYLE_REF_KEY]: styleId }]));
    return next;
};

/**
 * One section wearing another's appearance: all five style bags, plus nothing else.
 *
 * EMPTY bags where the source has none, never undefined. A section is saved with a Cosmos PATCH and
 * StorefrontSection.ToPatchRequest skips a bag that arrives null — deliberately, so a partial save cannot
 * wipe a bag it did not send. The consequence here is that copying a PLAIN section's appearance onto a
 * styled one has to send `{}` to clear it: leaving the key undefined drops it from the request, the patch
 * skips it, and the target keeps the styling it was supposed to lose. In the editor it looks applied,
 * because the working draft really did set undefined; the old styling only comes back on the save. Same
 * trap, and the same fix, as ApplySectionStyling's Bag() on the server.
 */
export const sectionWithAppearanceOf = (
    target: IStorefrontSection,
    source: IStorefrontSection,
): IStorefrontSection => ({
    ...target,
    styleProps: source.styleProps ?? {},
    titleStyleProps: source.titleStyleProps ?? {},
    headerStyleProps: source.headerStyleProps ?? {},
    footerStyleProps: source.footerStyleProps ?? {},
    productStyleProps: source.productStyleProps ?? {},
    // Empty rather than undefined for the same reason as the bags above: this field follows the identical
    // null-versus-empty patch contract on the server, so undefined would be dropped by the save and leave
    // the target's own overrides in place.
    styleOverrides: source.styleOverrides ?? {},
});

/**
 * The named `section` style this section already follows, if any.
 *
 * Preferring `styleProps` (the band) because that is what `sectionWithStyleRef` writes first and what a
 * section pointed at a style always carries; the other bags are checked after it so a section assembled
 * through the per-bag pickers is still recognised. Only a `section` style counts — a bag may legitimately
 * reference a CARD style (`productStyleProps` does), and that is not a style of this section.
 *
 * Callers use this to offer UPDATING the style rather than silently saving a second copy of it under the
 * same name.
 */
export const currentSectionStyle = (
    section: IStorefrontSection,
    styles: IStorefrontStyle[] | undefined,
): IStorefrontStyle | undefined => {
    for (const bag of SECTION_STYLE_BAGS) {
        const found = findStyle(styles, getStyleRef(section[bag]));
        if (found?.elementType === 'section') return found;
    }
    return undefined;
};

/**
 * What a section looks like right now, shaped as a section style's parts — the other direction.
 *
 * Takes the section's effective appearance, not just its local overrides: a section already built on a
 * named style has most of its look in that style's parts, and capturing only what sits on top would save an
 * almost-empty style that looks nothing like the section it was taken from. So the referenced style's part
 * goes underneath and the section's own properties win, which is the same order the renderer resolves in.
 *
 * Palette and font TOKENS are deliberately left as tokens. Resolving them (as the render path does) would
 * bake today's palette into the saved style, and a style whose colors cannot follow the palette is the one
 * thing a reusable style must not be.
 */
export const capturedSectionParts = (
    section: IStorefrontSection,
    styles: IStorefrontStyle[] | undefined,
    /**
     * The page configuration the section is currently sitting in, parsed. Supplied so the capture STAMPS
     * the page-level styling that affects this section (AC-1165) — the card, its name and price, the
     * add-to-cart button, the knocked-out title block. Without it a captured style carries the section's
     * container and nothing that paints inside it, which is exactly how a saved style came to look
     * nothing like the section it was taken from. Omitted = capture the section's own layers only.
     */
    pageConfig?: { [configKey: string]: unknown },
): IStorefrontStyleParts => {
    const parts: IStorefrontStyleParts = {};
    for (const bag of SECTION_STYLE_BAGS) {
        const part = partForBag(bag);
        if (!part) continue;
        const local = section[bag];
        const ref = getStyleRef(local);
        const preset = ref ? findStyle(styles, ref)?.parts?.[part] : undefined;
        parts[part] = { ...(preset ?? {}), ...(stripStyleRef(local) ?? {}) };
    }
    for (const key of SECTION_OVERRIDE_KEYS) {
        const part = SECTION_OVERRIDE_PARTS[key];
        const local = section.styleOverrides?.[key];
        const ref = getStyleRef(local);
        const preset = ref ? findStyle(styles, ref)?.parts?.[part] : undefined;
        // Page value underneath, then the style the override references, then the override's own
        // properties — the same order the render resolves in, so what is captured is what was on screen.
        const captured = {
            ...((pageConfig?.[key] ?? {}) as Record<string, unknown>),
            ...(preset ?? {}),
            ...(stripStyleRef(local) ?? {}),
        };
        // An empty part is noise in a style doc, and `sectionHasAppearance` counts parts.
        if (Object.keys(captured).length > 0) parts[part] = captured;
    }
    return parts;
};

/** Whether a section has any appearance worth saving — an empty style helps nobody. */
export const sectionHasAppearance = (
    section: IStorefrontSection,
    styles: IStorefrontStyle[] | undefined,
    pageConfig?: { [configKey: string]: unknown },
): boolean =>
    Object.values(capturedSectionParts(section, styles, pageConfig))
        .some((part) => Object.keys(part ?? {}).length > 0);
